import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Select,
  Segmented,
  DatePicker,
  Row,
  Col,
  Typography,
  Statistic,
  Table,
  Tag,
  Alert,
  Empty,
  Spin,
  message
} from 'antd';
import {
  LineChartOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  MinusOutlined
} from '@ant-design/icons';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend
} from 'recharts';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { productosService } from '../services/api';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const formatMoney = (v, digits = 2) => {
  if (v == null || Number.isNaN(Number(v))) return '—';
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  }).format(v);
};

function TendenciasCostos() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [productos, setProductos] = useState([]);
  const [productoId, setProductoId] = useState(() => {
    const p = searchParams.get('producto');
    return p ? Number(p) : null;
  });
  const [rango, setRango] = useState(null);
  const [modo, setModo] = useState('compra'); // 'compra' | 'tendencia'

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    productosService
      .getAll({ activo: 1 })
      .then((res) => setProductos(res.data.data || []))
      .catch((e) => message.error(e.displayMessage || 'Error al cargar productos'));
  }, []);

  const fetchTendencia = useCallback(async () => {
    if (!productoId) {
      setData(null);
      return;
    }
    setLoading(true);
    try {
      const params = {};
      if (rango && rango[0] && rango[1]) {
        params.desde = rango[0].format('YYYY-MM-DD');
        params.hasta = rango[1].format('YYYY-MM-DD');
      }
      const res = await productosService.getTendenciaCostos(productoId, params);
      setData(res.data.data);
    } catch (e) {
      message.error(e.displayMessage || 'Error al cargar la tendencia de costos');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [productoId, rango]);

  useEffect(() => {
    fetchTendencia();
  }, [fetchTendencia]);

  const handleProducto = (value) => {
    setProductoId(value || null);
    setModo('compra');
    const next = new URLSearchParams(searchParams);
    if (value) next.set('producto', value);
    else next.delete('producto');
    setSearchParams(next, { replace: true });
  };

  const producto = data?.producto;
  const puntos = useMemo(() => data?.puntos || [], [data]);

  const tieneFactor =
    !!producto?.factor_unidad_tendencia || puntos.some((p) => p.factor_aplicado);

  const unidadCompra = producto?.unidad_medida || 'unidad';
  const unidadTendencia = producto?.unidad_tendencia || null;

  // Si el producto no tiene unidad de tendencia configurada, forzamos modo compra.
  useEffect(() => {
    if (!tieneFactor && modo === 'tendencia') setModo('compra');
  }, [tieneFactor, modo]);

  const campo = modo === 'tendencia' ? 'costo_normalizado' : 'costo_unitario';
  const unidadActual = modo === 'tendencia' ? unidadTendencia : unidadCompra;

  const chartData = useMemo(
    () =>
      puntos
        .filter((p) => p[campo] != null)
        .map((p, i) => ({
          key: `${p.detalle_id}-${i}`,
          fechaLabel: dayjs(p.fecha).format('DD/MM/YY'),
          fecha: p.fecha,
          valor: Number(p[campo]),
          numero_orden: p.numero_orden,
          proveedor: p.proveedor
        })),
    [puntos, campo]
  );

  const stats = useMemo(() => {
    if (chartData.length === 0) return null;
    const valores = chartData.map((d) => d.valor);
    const primero = valores[0];
    const ultimo = valores[valores.length - 1];
    const min = Math.min(...valores);
    const max = Math.max(...valores);
    const variacionPct = primero > 0 ? ((ultimo - primero) / primero) * 100 : 0;
    return { primero, ultimo, min, max, variacionPct, n: valores.length };
  }, [chartData]);

  const digits = modo === 'tendencia' ? 4 : 2;

  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      render: (f) => dayjs(f).format('DD/MM/YYYY'),
      sorter: (a, b) => dayjs(a.fecha).valueOf() - dayjs(b.fecha).valueOf(),
      defaultSortOrder: 'ascend'
    },
    { title: 'Orden', dataIndex: 'numero_orden', key: 'numero_orden', render: (v) => <Text code>{v}</Text> },
    { title: 'Proveedor', dataIndex: 'proveedor', key: 'proveedor', render: (v) => v || '—' },
    {
      title: 'Cantidad',
      dataIndex: 'cantidad',
      key: 'cantidad',
      align: 'right',
      render: (v) => `${Number(v).toLocaleString('es-MX')} ${unidadCompra}`
    },
    {
      title: `Costo / ${unidadCompra}`,
      dataIndex: 'costo_unitario',
      key: 'costo_unitario',
      align: 'right',
      render: (v) => formatMoney(v, 2)
    },
    {
      title: 'Factor',
      dataIndex: 'factor_aplicado',
      key: 'factor_aplicado',
      align: 'right',
      render: (v) =>
        v ? (
          <Tag color="blue">
            1 {unidadCompra} = {Number(v).toLocaleString('es-MX')} {unidadTendencia || '?'}
          </Tag>
        ) : (
          <Text type="secondary">—</Text>
        )
    },
    {
      title: unidadTendencia ? `Costo / ${unidadTendencia}` : 'Costo normalizado',
      dataIndex: 'costo_normalizado',
      key: 'costo_normalizado',
      align: 'right',
      render: (v) => (v != null ? formatMoney(v, 4) : <Text type="secondary">—</Text>)
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          <LineChartOutlined /> Tendencia de Costos
        </Title>
        <Text type="secondary">
          Evolución del costo de compra de cada producto a lo largo del tiempo
        </Text>
      </div>

      <Card bordered={false} style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={10} lg={8}>
            <Select
              placeholder="Selecciona un producto"
              style={{ width: '100%' }}
              showSearch
              allowClear
              value={productoId || undefined}
              onChange={handleProducto}
              optionFilterProp="label"
              options={productos.map((p) => ({
                value: p.id,
                label: `${p.clave} — ${p.nombre}`
              }))}
            />
          </Col>
          <Col xs={24} md={9} lg={8}>
            <RangePicker
              style={{ width: '100%' }}
              value={rango}
              onChange={setRango}
              format="DD/MM/YYYY"
              allowClear
            />
          </Col>
          <Col xs={24} md={5} lg={8}>
            <Segmented
              block
              value={modo}
              onChange={setModo}
              options={[
                { label: `Por ${unidadCompra}`, value: 'compra' },
                {
                  label: unidadTendencia ? `Por ${unidadTendencia}` : 'Normalizado',
                  value: 'tendencia',
                  disabled: !tieneFactor
                }
              ]}
            />
          </Col>
        </Row>
      </Card>

      {!productoId && (
        <Card bordered={false}>
          <Empty description="Selecciona un producto para ver su tendencia de costos" />
        </Card>
      )}

      {productoId && (
        <Spin spinning={loading}>
          {producto && !tieneFactor && (
            <Alert
              style={{ marginBottom: 16 }}
              type="info"
              showIcon
              message="Este producto no tiene unidad de tendencia configurada"
              description={
                <>
                  Se compra por <b>{unidadCompra}</b>. Si su costo lo mueve el mercado en otra
                  unidad (ej. acero comprado por PZA pero cotizado por KG), edita el producto y
                  define <b>Unidad de tendencia</b> y el <b>Factor de conversión</b> (cuántos KG
                  pesa 1 PZA). A partir de ahí podrás ver el costo normalizado y compararlo entre
                  compras.
                </>
              }
            />
          )}

          {modo === 'tendencia' && (
            <Alert
              style={{ marginBottom: 16 }}
              type="warning"
              showIcon
              message={`Costo normalizado a ${unidadTendencia}`}
              description={`Se divide el costo de cada compra entre el factor de conversión vigente en esa compra. Útil para comparar contra el precio de mercado del ${unidadTendencia}.`}
            />
          )}

          {stats && (
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={12} md={6}>
                <Card bordered={false} size="small">
                  <Statistic
                    title={`Último costo / ${unidadActual || '—'}`}
                    value={stats.ultimo}
                    precision={digits}
                    prefix="$"
                  />
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card bordered={false} size="small">
                  <Statistic
                    title="Variación en el periodo"
                    value={Math.abs(stats.variacionPct)}
                    precision={1}
                    suffix="%"
                    valueStyle={{
                      color:
                        stats.variacionPct > 0.05
                          ? '#cf1322'
                          : stats.variacionPct < -0.05
                          ? '#3f8600'
                          : undefined
                    }}
                    prefix={
                      stats.variacionPct > 0.05 ? (
                        <ArrowUpOutlined />
                      ) : stats.variacionPct < -0.05 ? (
                        <ArrowDownOutlined />
                      ) : (
                        <MinusOutlined />
                      )
                    }
                  />
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card bordered={false} size="small">
                  <Statistic title="Mínimo" value={stats.min} precision={digits} prefix="$" />
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card bordered={false} size="small">
                  <Statistic title="Máximo" value={stats.max} precision={digits} prefix="$" />
                </Card>
              </Col>
            </Row>
          )}

          <Card
            bordered={false}
            style={{ marginBottom: 16 }}
            title={
              producto
                ? `${producto.clave} — ${producto.nombre}`
                : 'Cargando…'
            }
          >
            {chartData.length === 0 ? (
              <Empty description="No hay compras registradas para este producto en el periodo seleccionado" />
            ) : (
              <ResponsiveContainer width="100%" height={360}>
                <LineChart data={chartData} margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="fechaLabel" tick={{ fontSize: 12 }} />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    width={80}
                    domain={['auto', 'auto']}
                    tickFormatter={(v) => formatMoney(v, digits)}
                  />
                  <RTooltip
                    formatter={(v) => [formatMoney(v, digits), `Costo / ${unidadActual || 'unidad'}`]}
                    labelFormatter={(l) => `Fecha: ${l}`}
                    content={({ active, payload }) => {
                      if (!active || !payload || !payload.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div
                          style={{
                            background: '#fff',
                            border: '1px solid #d9d9d9',
                            borderRadius: 6,
                            padding: '8px 12px',
                            fontSize: 12
                          }}
                        >
                          <div><b>{dayjs(d.fecha).format('DD/MM/YYYY')}</b></div>
                          <div>Orden: {d.numero_orden}</div>
                          {d.proveedor && <div>Proveedor: {d.proveedor}</div>}
                          <div style={{ marginTop: 4 }}>
                            Costo / {unidadActual || 'unidad'}: <b>{formatMoney(d.valor, digits)}</b>
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="valor"
                    name={`Costo / ${unidadActual || 'unidad'}`}
                    stroke="#1677ff"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>

          <Card bordered={false} title="Detalle de compras">
            <Table
              size="small"
              rowKey="detalle_id"
              dataSource={puntos}
              columns={columns}
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
              locale={{ emptyText: 'Sin compras en el periodo' }}
              scroll={{ x: 800 }}
            />
          </Card>
        </Spin>
      )}
    </div>
  );
}

export default TendenciasCostos;
